import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { adminApi, getStaticUrl, gamesApi, invitationsApi, permissionsApi, rolesApi } from '../../api/client';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, TrashIcon, CloseIcon, GamepadIcon, EditIcon, ClipboardIcon, CheckIcon } from '../Icons/Icons';
import './AdminPanel.css';

const ROLE_LABELS = {
  player: 'Joueur',
  coach: 'Coach',
  manager: 'Manager',
  responsable: 'Responsable',
  ceo: 'CEO',
  coo: 'COO',
  admin: 'Admin',
};

const AdminPanel = () => {
  const { user, isAdmin, hasPermission } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('users');

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedGameIds, setSelectedGameIds] = useState([]);
  const [uploadingAvatar, setUploadingAvatar] = useState(null);

  const [games, setGames] = useState([]);
  const [showGameForm, setShowGameForm] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [gameFormData, setGameFormData] = useState({ name: '', category: '' });
  const [gamesLoading, setGamesLoading] = useState(false);
  const [gamesError, setGamesError] = useState('');
  const [gameLogoFile, setGameLogoFile] = useState(null);
  const [gameLogoPreview, setGameLogoPreview] = useState(null);
  const [gameLogoRemoved, setGameLogoRemoved] = useState(false);

  const [invitations, setInvitations] = useState([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [invitationsError, setInvitationsError] = useState('');
  const [copiedToken, setCopiedToken] = useState(null);

  const [permissionsData, setPermissionsData] = useState(null);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsError, setPermissionsError] = useState('');
  const [permissionChanges, setPermissionChanges] = useState([]);
  const [savingPermissions, setSavingPermissions] = useState(false);

  const [rolesList, setRolesList] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState('');
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleFormData, setRoleFormData] = useState({ name: '', description: '', color: '#6b7280' });

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
      return;
    }
    loadUsers();
    loadGames();
    loadRoles();
    if (activeTab === 'invitations') loadInvitations();
    if (activeTab === 'permissions') loadPermissions();
  }, [isAdmin, navigate, activeTab]);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.getAllUsers();
      setUsers(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const loadGames = async () => {
    try {
      setGamesLoading(true);
      const res = await gamesApi.getAll();
      setGames(res.data);
      setGamesError('');
    } catch (error) {
      setGamesError('Erreur lors du chargement des jeux');
    } finally {
      setGamesLoading(false);
    }
  };

  const loadInvitations = async () => {
    try {
      setInvitationsLoading(true);
      const res = await invitationsApi.getAll();
      setInvitations(res.data);
      setInvitationsError('');
    } catch (error) {
      setInvitationsError('Erreur lors du chargement des invitations');
    } finally {
      setInvitationsLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      setPermissionsLoading(true);
      const res = await permissionsApi.getAll();
      setPermissionsData(res.data);
      setPermissionChanges([]);
      setPermissionsError('');
    } catch (error) {
      setPermissionsError('Erreur lors du chargement des permissions');
    } finally {
      setPermissionsLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      setRolesLoading(true);
      const res = await rolesApi.getAll();
      setRolesList(res.data);
      setRolesError('');
    } catch (error) {
      setRolesError('Erreur lors du chargement des rôles');
    } finally {
      setRolesLoading(false);
    }
  };

  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    setRolesLoading(true);
    setRolesError('');
    try {
      if (editingRole) {
        await rolesApi.update(editingRole.id, roleFormData);
      } else {
        await rolesApi.create(roleFormData);
      }
      cancelRoleItemEdit();
      await loadRoles();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Erreur';
      setRolesError(errorMsg);
      alert('Erreur: ' + errorMsg);
    } finally {
      setRolesLoading(false);
    }
  };

  const handleEditRoleItem = (role) => {
    setEditingRole(role);
    setRoleFormData({ name: role.name, description: role.description || '', color: role.color || '#6b7280' });
    setShowRoleForm(true);
  };

  const cancelRoleItemEdit = () => {
    setEditingRole(null);
    setRoleFormData({ name: '', description: '', color: '#6b7280' });
    setShowRoleForm(false);
  };

  const handleDeleteRoleItem = async (roleId) => {
    if (!window.confirm('Supprimer ce rôle ?')) return;
    try {
      setRolesLoading(true);
      await rolesApi.delete(roleId);
      await loadRoles();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setRolesLoading(false);
    }
  };

  const handlePermissionToggle = (role, permissionKey, currentValue) => {
    const newValue = !currentValue;
    setPermissionsData(prev => {
      const updated = { ...prev };
      updated.permissions = updated.permissions.map(p => {
        if (p.key === permissionKey) return { ...p, roles: { ...p.roles, [role]: newValue } };
        return p;
      });
      return updated;
    });
    setPermissionChanges(prev => {
      const existing = prev.findIndex(c => c.role === role && c.permission_key === permissionKey);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { role, permission_key: permissionKey, granted: newValue };
        return updated;
      }
      return [...prev, { role, permission_key: permissionKey, granted: newValue }];
    });
  };

  const handleSavePermissions = async () => {
    if (permissionChanges.length === 0) return;
    setSavingPermissions(true);
    try {
      await permissionsApi.update(permissionChanges);
      setPermissionChanges([]);
      await loadPermissions();
    } catch (error) {
      alert('Erreur lors de la sauvegarde des permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleCreateInvitation = async () => {
    try {
      const res = await invitationsApi.create();
      setInvitations([res.data, ...invitations]);
    } catch (error) {
      alert('Erreur lors de la création de l\'invitation');
    }
  };

  const handleCopyInvitationLink = (token) => {
    const link = `${window.location.origin}/inscription/${token}`;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link)
        .then(() => { setCopiedToken(token); setTimeout(() => setCopiedToken(null), 2000); })
        .catch(() => fallbackCopyToClipboard(link, token));
    } else {
      fallbackCopyToClipboard(link, token);
    }
  };

  const fallbackCopyToClipboard = (text, token) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch {
      alert(`Copiez ce lien manuellement: ${text}`);
    }
    document.body.removeChild(textArea);
  };

  const handleDeleteInvitation = async (invitationId) => {
    if (!window.confirm('Supprimer cette invitation ?')) return;
    try {
      await invitationsApi.delete(invitationId);
      setInvitations(invitations.filter(inv => inv.id !== invitationId));
    } catch (error) {
      alert('Erreur lors de la suppression de l\'invitation');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cet utilisateur ?')) return;
    try {
      await adminApi.deleteUser(userId);
      setUsers(users.filter(u => u.id !== userId));
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la suppression de l\'utilisateur');
    }
  };

  const handleEditRole = (u) => {
    setEditingUser(u);
    setSelectedRole(u.role);
    setSelectedGameIds(u.responsable_game_ids || []);
  };

  const handleSaveRole = async () => {
    if (!editingUser) return;
    if (selectedRole === 'responsable' && selectedGameIds.length === 0) {
      alert('Vous devez sélectionner au moins un jeu pour le rôle Responsable');
      return;
    }
    try {
      const response = await adminApi.updateUserRole(
        editingUser.id,
        selectedRole,
        selectedRole === 'responsable' ? selectedGameIds : []
      );
      setUsers(users.map(u => u.id === editingUser.id ? response.data : u));
      setEditingUser(null);
      setSelectedRole('');
      setSelectedGameIds([]);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la mise à jour du rôle');
    }
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setSelectedRole('');
    setSelectedGameIds([]);
  };

  const handleAvatarUpload = async (userId, event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Le fichier est trop volumineux. Taille maximale : 5MB');
      return;
    }
    setUploadingAvatar(userId);
    try {
      await adminApi.uploadAvatar(userId, file);
      await loadUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de l\'upload de l\'avatar');
    } finally {
      setUploadingAvatar(null);
    }
  };

  const handleGameSubmit = async (e) => {
    e.preventDefault();
    setGamesLoading(true);
    setGamesError('');
    try {
      let gameId;
      const submitData = { ...gameFormData, category: gameFormData.category || null };
      if (editingGame) {
        await gamesApi.update(editingGame.id, submitData);
        gameId = editingGame.id;
      } else {
        const res = await gamesApi.create(submitData);
        gameId = res.data.id;
      }
      if (gameLogoFile) {
        await gamesApi.uploadLogo(gameId, gameLogoFile);
      } else if (gameLogoRemoved && editingGame?.logo) {
        await gamesApi.deleteLogo(gameId);
      }
      setGameFormData({ name: '', category: '' });
      setGameLogoFile(null);
      setGameLogoPreview(null);
      setGameLogoRemoved(false);
      setShowGameForm(false);
      setEditingGame(null);
      await loadGames();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Erreur lors de la création';
      setGamesError(errorMsg);
      alert('Erreur: ' + errorMsg);
    } finally {
      setGamesLoading(false);
    }
  };

  const handleEditGame = (game) => {
    setEditingGame(game);
    setGameFormData({ name: game.name, category: game.category || '' });
    setGameLogoFile(null);
    setGameLogoPreview(game.logo ? getStaticUrl(game.logo) : null);
    setGameLogoRemoved(false);
    setShowGameForm(true);
  };

  const cancelGameEdit = () => {
    setEditingGame(null);
    setGameFormData({ name: '', category: '' });
    setGameLogoFile(null);
    setGameLogoPreview(null);
    setGameLogoRemoved(false);
    setShowGameForm(false);
  };

  const handleDeleteGame = async (id) => {
    if (window.confirm('Supprimer ce jeu?')) {
      try {
        setGamesLoading(true);
        await gamesApi.delete(id);
        await loadGames();
      } catch (error) {
        alert(error.response?.data?.detail || 'Erreur lors de la suppression');
      } finally {
        setGamesLoading(false);
      }
    }
  };

  const getRoleBadgeClass = (role) => {
    const map = {
      player: 'ap-role-badge ap-role-player',
      coach: 'ap-role-badge ap-role-coach',
      manager: 'ap-role-badge ap-role-manager',
      responsable: 'ap-role-badge ap-role-responsable',
      ceo: 'ap-role-badge ap-role-ceo',
      coo: 'ap-role-badge ap-role-coo',
      admin: 'ap-role-badge ap-role-admin',
    };
    return map[role] || 'ap-role-badge';
  };

  const getRoleLabel = (role) => {
    const roleObj = rolesList.find(r => r.value === role);
    return roleObj?.name || ROLE_LABELS[role] || role;
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  const HIDDEN_ROLES = ['ceo', 'coo', 'admin'];
  const getDisplayedRoles = () => {
    if (!permissionsData) return [];
    return permissionsData.roles.filter(r => !HIDDEN_ROLES.includes(r));
  };

  const getGroupedPermissions = () => {
    if (!permissionsData) return {};
    const grouped = {};
    for (const perm of permissionsData.permissions) {
      if (!grouped[perm.category]) grouped[perm.category] = [];
      grouped[perm.category].push(perm);
    }
    return grouped;
  };

  const tabs = [
    { id: 'users', label: 'Utilisateurs', icon: '👤' },
    { id: 'games', label: 'Jeux', icon: '🎮' },
    { id: 'invitations', label: 'Invitations', icon: '🔗' },
    ...(hasPermission('admin.manage_permissions') ? [
      { id: 'roles', label: 'Rôles', icon: '🏷️' },
      { id: 'permissions', label: 'Droits', icon: '🔑' },
    ] : []),
  ];

  if (loading) {
    return (
      <div className="ap-panel">
        <div className="ap-loading">
          <div className="ap-loading-spinner" />
          <span>Chargement...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ap-panel">
        <div className="ap-error-state">
          <p>{error}</p>
          <button onClick={loadUsers} className="btn-primary">Réessayer</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ap-panel">

      {/* Header */}
      <div className="ap-header">
        <div>
          <h1 className="ap-title">Administration</h1>
          <p className="ap-subtitle">Gestion des utilisateurs, rôles, jeux et invitations</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="ap-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`ap-tab${activeTab === tab.id ? ' ap-tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="ap-tab-icon">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── UTILISATEURS ─────────────────────────────── */}
      {activeTab === 'users' && (
        <div className="ap-section">
          <div className="ap-table-wrap">
            <table className="ap-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Avatar</th>
                  <th>Utilisateur</th>
                  <th>Rôle</th>
                  <th>Inscription</th>
                  <th>Dernière connexion</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className={u.id === user?.id ? 'ap-tr--self' : ''}>
                    <td className="ap-td-id">#{u.id}</td>
                    <td>
                      <div className="ap-avatar-cell">
                        {u.avatar ? (
                          <img src={getStaticUrl(u.avatar)} alt={u.username} className="ap-avatar" />
                        ) : (
                          <div className="ap-avatar-placeholder">
                            {u.username?.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <label className="ap-avatar-upload" title="Changer l'avatar">
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={(e) => handleAvatarUpload(u.id, e)}
                          />
                          {uploadingAvatar === u.id ? '⏳' : '📷'}
                        </label>
                      </div>
                    </td>
                    <td>
                      <span className="ap-username">{u.username}</span>
                      {u.id === user?.id && <span className="ap-badge-you">Vous</span>}
                    </td>
                    <td>
                      {editingUser?.id === u.id ? (
                        <div className="ap-role-edit">
                          <select
                            value={selectedRole}
                            onChange={(e) => {
                              setSelectedRole(e.target.value);
                              if (e.target.value !== 'responsable') setSelectedGameIds([]);
                            }}
                            className="ap-select"
                          >
                            {rolesList.filter(r => r.value && r.value !== 'admin').map(r => (
                              <option key={r.value} value={r.value}>{r.name}</option>
                            ))}
                          </select>
                          {selectedRole === 'responsable' && (
                            <div className="ap-game-checks">
                              {games.map(g => (
                                <label key={g.id} className="ap-game-check">
                                  <input
                                    type="checkbox"
                                    checked={selectedGameIds.includes(g.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) setSelectedGameIds([...selectedGameIds, g.id]);
                                      else setSelectedGameIds(selectedGameIds.filter(id => id !== g.id));
                                    }}
                                  />
                                  {g.name}
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <span className={getRoleBadgeClass(u.role)}>
                            {getRoleLabel(u.role)}
                          </span>
                          {u.role === 'responsable' && u.responsable_game_ids?.length > 0 && (
                            <div className="ap-game-tags">
                              {u.responsable_game_ids.map(gid => {
                                const game = games.find(g => g.id === gid);
                                return game ? <span key={gid} className="ap-game-tag">{game.name}</span> : null;
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="ap-td-date">{formatDate(u.created_at)}</td>
                    <td className="ap-td-date">
                      {u.last_login
                        ? formatDate(u.last_login)
                        : <span className="ap-never">Jamais</span>}
                    </td>
                    <td className="ap-td-actions">
                      {editingUser?.id === u.id ? (
                        <>
                          <button onClick={handleSaveRole} className="btn-save btn-sm">Sauvegarder</button>
                          <button onClick={handleCancelEdit} className="btn-cancel btn-sm">Annuler</button>
                        </>
                      ) : u.id !== user?.id ? (
                        <>
                          <button
                            onClick={() => handleEditRole(u)}
                            className="btn-edit btn-icon"
                            title="Modifier le rôle"
                          >
                            <EditIcon size={15} />
                          </button>
                          {hasPermission('admin.delete_users') && (
                            <button
                              onClick={() => handleDeleteUser(u.id)}
                              className="btn-delete btn-icon"
                              title="Supprimer"
                            >
                              <TrashIcon size={15} />
                            </button>
                          )}
                        </>
                      ) : (
                        <span className="ap-dash">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {users.length === 0 && (
            <div className="ap-empty">Aucun utilisateur trouvé</div>
          )}
        </div>
      )}

      {/* ── JEUX ─────────────────────────────────────── */}
      {activeTab === 'games' && (
        <div className="ap-section">
          <div className="ap-section-head">
            <div>
              <h2 className="ap-section-title">Jeux</h2>
              <p className="ap-section-sub">{games.length} jeu{games.length !== 1 ? 'x' : ''} enregistré{games.length !== 1 ? 's' : ''}</p>
            </div>
            <button
              onClick={() => showGameForm ? cancelGameEdit() : setShowGameForm(true)}
              className="btn-primary"
              disabled={gamesLoading}
            >
              {showGameForm ? <><CloseIcon size={16} /> Annuler</> : <><PlusIcon size={16} /> Ajouter un jeu</>}
            </button>
          </div>

          {gamesError && <div className="ap-error-banner">{gamesError}</div>}

          {showGameForm && (
            <div className="ap-form-card">
              <h3 className="ap-form-title">{editingGame ? 'Modifier le jeu' : 'Nouveau jeu'}</h3>
              <form onSubmit={handleGameSubmit} className="ap-game-form">
                <div className="ap-logo-wrap">
                  <label className="ap-logo-area" htmlFor="game-logo-input">
                    {gameLogoPreview ? (
                      <img src={gameLogoPreview} alt="Logo" className="ap-logo-img" />
                    ) : (
                      <div className="ap-logo-placeholder">
                        <GamepadIcon size={24} />
                        <span>Logo</span>
                      </div>
                    )}
                  </label>
                  <input
                    id="game-logo-input"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) { setGameLogoFile(file); setGameLogoPreview(URL.createObjectURL(file)); }
                    }}
                    disabled={gamesLoading}
                  />
                  {gameLogoPreview && (
                    <button
                      type="button"
                      className="ap-logo-remove"
                      onClick={() => { setGameLogoFile(null); setGameLogoPreview(null); setGameLogoRemoved(true); }}
                    >
                      <CloseIcon size={12} />
                    </button>
                  )}
                </div>
                <div className="ap-game-inputs">
                  <input
                    type="text"
                    placeholder="Nom du jeu *"
                    value={gameFormData.name}
                    onChange={(e) => setGameFormData({...gameFormData, name: e.target.value})}
                    required
                    disabled={gamesLoading}
                    className="ap-input"
                  />
                  <select
                    value={gameFormData.category}
                    onChange={(e) => setGameFormData({...gameFormData, category: e.target.value})}
                    disabled={gamesLoading}
                    className="ap-input"
                  >
                    <option value="">Catégorie (facultatif)</option>
                    <option value="FPS">FPS</option>
                    <option value="MOBA">MOBA</option>
                    <option value="Battle Royale">Battle Royale</option>
                    <option value="Fighting">Combat</option>
                    <option value="Sports">Sport</option>
                    <option value="Strategy">Stratégie</option>
                  </select>
                  <div className="ap-form-actions">
                    <button type="submit" className="btn-primary" disabled={gamesLoading}>
                      {gamesLoading
                        ? (editingGame ? 'Modification...' : 'Création...')
                        : (editingGame ? 'Mettre à jour' : 'Créer le jeu')}
                    </button>
                    {editingGame && (
                      <button type="button" onClick={cancelGameEdit} className="btn-cancel" disabled={gamesLoading}>
                        Annuler
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          )}

          <div className="ap-games-grid">
            {games.map(game => (
              <div key={game.id} className="ap-game-card">
                <div className="ap-game-icon">
                  {game.logo
                    ? <img src={getStaticUrl(game.logo)} alt={game.name} />
                    : <GamepadIcon size={28} />}
                </div>
                <div className="ap-game-info">
                  <h3 className="ap-game-name">{game.name}</h3>
                  {game.category && <span className="ap-game-cat">{game.category}</span>}
                </div>
                <div className="ap-game-actions">
                  <button
                    onClick={() => handleEditGame(game)}
                    className="btn-edit btn-icon"
                    disabled={gamesLoading}
                    title="Modifier ce jeu"
                  >
                    <EditIcon size={15} />
                  </button>
                  <button
                    onClick={() => handleDeleteGame(game.id)}
                    className="btn-delete btn-icon"
                    disabled={gamesLoading}
                    title="Supprimer"
                  >
                    <TrashIcon size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {games.length === 0 && !gamesLoading && (
            <div className="ap-empty">Aucun jeu. Commencez par en ajouter un !</div>
          )}
        </div>
      )}

      {/* ── INVITATIONS ──────────────────────────────── */}
      {activeTab === 'invitations' && (
        <div className="ap-section">
          <div className="ap-section-head">
            <div>
              <h2 className="ap-section-title">Invitations</h2>
              <p className="ap-section-sub">
                {invitations.filter(i => !i.is_used).length} disponible{invitations.filter(i => !i.is_used).length !== 1 ? 's' : ''}
              </p>
            </div>
            <button onClick={handleCreateInvitation} className="btn-primary" disabled={invitationsLoading}>
              <PlusIcon size={16} /> Créer une invitation
            </button>
          </div>

          {invitationsError && <div className="ap-error-banner">{invitationsError}</div>}

          <div className="ap-table-wrap">
            <table className="ap-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Créé par</th>
                  <th>Date</th>
                  <th>Statut</th>
                  <th>Utilisé par</th>
                  <th>Date d'utilisation</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map(inv => (
                  <tr key={inv.id} className={inv.is_used ? 'ap-tr--used' : ''}>
                    <td className="ap-td-id">#{inv.id}</td>
                    <td>{inv.creator_username}</td>
                    <td className="ap-td-date">{new Date(inv.created_at).toLocaleString('fr-FR')}</td>
                    <td>
                      {inv.is_used
                        ? <span className="ap-badge-used">Utilisé</span>
                        : <span className="ap-badge-available">Disponible</span>}
                    </td>
                    <td>{inv.used_by_username || <span className="ap-never">—</span>}</td>
                    <td className="ap-td-date">
                      {inv.used_at ? new Date(inv.used_at).toLocaleString('fr-FR') : <span className="ap-never">—</span>}
                    </td>
                    <td className="ap-td-actions">
                      {!inv.is_used && (
                        <>
                          <button
                            onClick={() => handleCopyInvitationLink(inv.token)}
                            className="btn-icon ap-btn-copy"
                            title="Copier le lien"
                          >
                            {copiedToken === inv.token ? <CheckIcon size={15} /> : <ClipboardIcon size={15} />}
                          </button>
                          <button
                            onClick={() => handleDeleteInvitation(inv.id)}
                            className="btn-delete btn-icon"
                            title="Supprimer"
                          >
                            <TrashIcon size={15} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {invitations.length === 0 && !invitationsLoading && (
            <div className="ap-empty">Aucune invitation. Créez-en une pour inviter un nouveau membre !</div>
          )}
        </div>
      )}

      {/* ── RÔLES ────────────────────────────────────── */}
      {activeTab === 'roles' && hasPermission('admin.manage_permissions') && (
        <div className="ap-section">
          <div className="ap-section-head">
            <div>
              <h2 className="ap-section-title">Rôles</h2>
              <p className="ap-section-sub">{rolesList.length} rôle{rolesList.length !== 1 ? 's' : ''} configuré{rolesList.length !== 1 ? 's' : ''}</p>
            </div>
            <button
              onClick={() => {
                if (showRoleForm && !editingRole) cancelRoleItemEdit();
                else { setEditingRole(null); setRoleFormData({ name: '', description: '', color: '#6b7280' }); setShowRoleForm(true); }
              }}
              className="btn-primary"
              disabled={rolesLoading}
            >
              {showRoleForm && !editingRole ? <><CloseIcon size={16} /> Annuler</> : <><PlusIcon size={16} /> Ajouter un rôle</>}
            </button>
          </div>

          {rolesError && <div className="ap-error-banner">{rolesError}</div>}

          {showRoleForm && (
            <div className="ap-form-card">
              <h3 className="ap-form-title">{editingRole ? 'Modifier le rôle' : 'Nouveau rôle'}</h3>
              <form onSubmit={handleRoleSubmit} className="ap-role-form">
                <input
                  type="text"
                  placeholder="Nom du rôle *"
                  value={roleFormData.name}
                  onChange={(e) => setRoleFormData({...roleFormData, name: e.target.value})}
                  required
                  disabled={rolesLoading}
                  className="ap-input"
                />
                <input
                  type="text"
                  placeholder="Description (optionnel)"
                  value={roleFormData.description}
                  onChange={(e) => setRoleFormData({...roleFormData, description: e.target.value})}
                  disabled={rolesLoading}
                  className="ap-input"
                />
                <div className="ap-color-wrap">
                  <label className="ap-color-label">Couleur</label>
                  <input
                    type="color"
                    value={roleFormData.color}
                    onChange={(e) => setRoleFormData({...roleFormData, color: e.target.value})}
                    disabled={rolesLoading}
                    className="ap-color-input"
                  />
                  <span className="ap-color-preview" style={{ background: roleFormData.color }} />
                </div>
                <div className="ap-form-actions">
                  <button type="submit" className="btn-primary" disabled={rolesLoading}>
                    {rolesLoading ? 'Enregistrement...' : (editingRole ? 'Mettre à jour' : 'Créer le rôle')}
                  </button>
                  {editingRole && (
                    <button type="button" onClick={cancelRoleItemEdit} className="btn-cancel" disabled={rolesLoading}>
                      Annuler
                    </button>
                  )}
                </div>
              </form>
            </div>
          )}

          <div className="ap-table-wrap">
            <table className="ap-table">
              <thead>
                <tr>
                  <th>Rôle</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rolesList.map(role => (
                  <tr key={role.id}>
                    <td>
                      <div className="ap-role-name">
                        <span className="ap-role-dot" style={{ background: role.color || '#6b7280' }} />
                        <span style={{ color: role.color || 'var(--text-primary)' }}>{role.name}</span>
                      </div>
                    </td>
                    <td className="ap-td-desc">{role.description || <span className="ap-never">—</span>}</td>
                    <td className="ap-td-actions">
                      <button
                        onClick={() => handleEditRoleItem(role)}
                        className="btn-edit btn-icon"
                        disabled={rolesLoading}
                        title="Modifier ce rôle"
                      >
                        <EditIcon size={15} />
                      </button>
                      <button
                        onClick={() => handleDeleteRoleItem(role.id)}
                        className="btn-delete btn-icon"
                        disabled={rolesLoading}
                        title="Supprimer"
                      >
                        <TrashIcon size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {rolesList.length === 0 && !rolesLoading && (
            <div className="ap-empty">Aucun rôle configuré.</div>
          )}
        </div>
      )}

      {/* ── DROITS / PERMISSIONS ─────────────────────── */}
      {activeTab === 'permissions' && hasPermission('admin.manage_permissions') && (
        <div className="ap-section">
          <div className="ap-section-head">
            <div>
              <h2 className="ap-section-title">Droits par rôle</h2>
              <p className="ap-section-sub">
                {permissionChanges.length > 0
                  ? <span className="ap-changes-count">{permissionChanges.length} modification{permissionChanges.length > 1 ? 's' : ''} non sauvegardée{permissionChanges.length > 1 ? 's' : ''}</span>
                  : 'Gérez les permissions de chaque rôle'}
              </p>
            </div>
            {permissionChanges.length > 0 && (
              <button onClick={handleSavePermissions} className="btn-primary" disabled={savingPermissions}>
                {savingPermissions ? 'Sauvegarde...' : `Sauvegarder (${permissionChanges.length})`}
              </button>
            )}
          </div>

          {permissionsError && <div className="ap-error-banner">{permissionsError}</div>}

          {permissionsData && (
            <div className="ap-perms-wrap">
              <table className="ap-perms-table">
                <thead>
                  <tr>
                    <th className="ap-perm-col-label">Permission</th>
                    {getDisplayedRoles().map(role => {
                      const roleObj = rolesList.find(r => r.name === role);
                      return (
                        <th
                          key={role}
                          className="ap-perm-col-role"
                          style={roleObj?.color ? { borderBottom: `3px solid ${roleObj.color}` } : {}}
                        >
                          {roleObj?.name || ROLE_LABELS[role] || role}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(getGroupedPermissions()).map(([category, perms]) => (
                    <React.Fragment key={`cat-${category}`}>
                      <tr className="ap-perm-category-row">
                        <td colSpan={getDisplayedRoles().length + 1}>{category}</td>
                      </tr>
                      {perms.map(perm => (
                        <tr key={perm.key} className="ap-perm-row">
                          <td className="ap-perm-label">{perm.label}</td>
                          {getDisplayedRoles().map(role => (
                            <td key={`${perm.key}-${role}`} className="ap-perm-check-cell">
                              <input
                                type="checkbox"
                                checked={perm.roles[role] || false}
                                onChange={() => handlePermissionToggle(role, perm.key, perm.roles[role])}
                                className="ap-perm-checkbox"
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default AdminPanel;
